<template>
  <div class="clander-page">

    <!-- ── TOP BAR — month navigation only ──────────────────────────── -->
    <div class="clander-topbar">
      <button class="nav-arrow" @click="prevMonth">
        <ion-icon name="chevron-back-outline"></ion-icon>
      </button>
      <div class="topbar-month-center">
        <span class="month-label">{{ currentMonthLabel }}</span>
        <button class="today-chip" @click="goToday" title="Go to today">Today</button>
      </div>
      <button class="nav-arrow" @click="nextMonth">
        <ion-icon name="chevron-forward-outline"></ion-icon>
      </button>
    </div>

    <!-- ── ACTIVE FILTER CHIPS ───────────────────────────────────────────── -->
    <div v-if="isFilterActive" class="filter-chips-row">
      <span v-if="filters.wing" class="filter-chip">
        <ion-icon name="people-circle-outline"></ion-icon>
        {{ wingLabels[filters.wing] }}
        <button @click="clearWingFilter" class="chip-close"><ion-icon name="close-outline"></ion-icon></button>
      </span>
      <span v-if="filters.unit_id && unitName" class="filter-chip">
        <ion-icon name="home-outline"></ion-icon>
        {{ unitName }}
        <button @click="clearLocationFilter('unit')" class="chip-close"><ion-icon name="close-outline"></ion-icon></button>
      </span>
      <span v-else-if="filters.zone_id && zoneName" class="filter-chip">
        <ion-icon name="map-outline"></ion-icon>
        {{ zoneName }}
        <button @click="clearLocationFilter('zone')" class="chip-close"><ion-icon name="close-outline"></ion-icon></button>
      </span>
      <span v-else-if="filters.district_id && districtName" class="filter-chip">
        <ion-icon name="location-outline"></ion-icon>
        {{ districtName }}
        <button @click="clearLocationFilter('district')" class="chip-close"><ion-icon name="close-outline"></ion-icon></button>
      </span>
      <button class="chip-reset" @click="resetFilters">Reset</button>
    </div>

    <!-- ── VIEWING AS BANNER ────────────────────────────────────────────── -->
    <div v-if="targetMemberId" class="viewing-as-bar">
      <ion-icon name="person-outline"></ion-icon>
      Viewing: <strong>{{ targetMemberName }}</strong>
      <button @click="switchMember(null, 'My Calendar')" class="viewing-as-close">
        <ion-icon name="close-outline"></ion-icon>
      </button>
    </div>

    <!-- ── CALENDAR GRID ─────────────────────────────────────────────────── -->
    <div class="clander-grid-wrap">
      <!-- Day-of-week headers -->
      <div class="dow-row">
        <span v-for="d in ['Su','Mo','Tu','We','Th','Fr','Sa']" :key="d" class="dow-cell">{{ d }}</span>
      </div>

      <!-- Loading overlay on grid -->
      <div v-if="loading" class="grid-loading">
        <div class="spinner-border text-primary" role="status" style="width:24px;height:24px;"></div>
      </div>

      <!-- Week rows -->
      <div v-for="(wd, wi) in weeksWithData" :key="wi" class="week-row">

        <!-- Day numbers row -->
        <div class="week-day-nums">
          <div
            v-for="cell in wd.week"
            :key="cell.key"
            class="day-num-cell"
            :class="{ 'other-month': !cell.currentMonth }"
            @click="selectDate(cell.date)"
          >
            <span class="day-num" :class="{ today: cell.isToday, selected: cell.isSelected }">
              {{ cell.day }}
            </span>
          </div>
        </div>

        <!-- Multi-day events: one continuous bar per event, stops exactly at end column -->
        <div class="week-multiday" v-if="wd.multiDay.length > 0">
          <div
            v-for="e in wd.multiDay.slice(0, 3)"
            :key="'md-' + e.id + '-' + wi"
            class="multiday-bar"
            :style="{
              gridColumnStart: e.colStart + 1,
              gridColumnEnd:   e.colEnd + 2,
              background: e.color || '#3788d8',
            }"
            @click.stop="openEventDetail(e)"
          >{{ e.title }}</div>
          <div v-if="wd.multiDay.length > 3" class="week-more-bar">
            +{{ wd.multiDay.length - 3 }} more
          </div>
        </div>

        <!-- Single-day chips: each event stays strictly in its own column -->
        <div class="week-single-chips">
          <div
            v-for="(cell, di) in wd.week"
            :key="'sc-' + cell.key"
            class="cell-chip-col"
            @click="selectDate(cell.date)"
          >
            <div
              v-for="e in wd.singleDay[di].slice(0, 2)"
              :key="e.id"
              class="cell-event-chip"
              :style="{ background: e.color || '#3788d8' }"
              @click.stop="openEventDetail(e)"
            >{{ e.title }}</div>
            <div v-if="wd.singleDay[di].length > 2" class="cell-more">
              +{{ wd.singleDay[di].length - 2 }}
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ── FAB: ADD PERSONAL EVENT ────────────────────────────────────────── -->
    <button class="clander-fab" @click="openAddSheet()" title="Add personal event">
      <ion-icon name="add-outline"></ion-icon>
    </button>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!--  BOTTOM SHEETS                                                      -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->

    <!-- ── ADD / EDIT EVENT SHEET ──────────────────────────────────────── -->
    <div v-if="showAddSheet" class="sheet-overlay" @click.self="closeAddSheet">
      <div class="bottom-sheet bottom-sheet-form">
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <h3 class="sheet-title">{{ editingEvent ? 'Edit Event' : 'New Personal Event' }}</h3>
          <button class="sheet-close" @click="closeAddSheet">
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>
        <div class="sheet-body">
          <div class="form-group">
            <input
              v-model="eventForm.title"
              type="text"
              class="form-control"
              placeholder="Event title"
              :class="{ 'is-invalid': formErrors.title }"
            />
            <div class="invalid-feedback" v-if="formErrors.title">{{ formErrors.title }}</div>
          </div>

          <div class="form-group all-day-toggle-row">
            <label class="toggle-label">All day</label>
            <div class="form-check form-switch mb-0">
              <input class="form-check-input" type="checkbox" v-model="eventForm.is_all_day" id="allDaySwitch" />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label-sm">Start</label>
            <input
              v-model="eventForm.start_datetime"
              :type="eventForm.is_all_day ? 'date' : 'datetime-local'"
              class="form-control"
              :class="{ 'is-invalid': formErrors.start_datetime }"
            />
            <div class="invalid-feedback" v-if="formErrors.start_datetime">{{ formErrors.start_datetime }}</div>
          </div>
          <div class="form-group">
            <label class="form-label-sm">End</label>
            <input
              v-model="eventForm.end_datetime"
              :type="eventForm.is_all_day ? 'date' : 'datetime-local'"
              class="form-control"
              :class="{ 'is-invalid': formErrors.end_datetime }"
            />
            <div class="invalid-feedback" v-if="formErrors.end_datetime">{{ formErrors.end_datetime }}</div>
          </div>

          <div class="form-group">
            <label class="form-label-sm">Color</label>
            <div class="color-swatches">
              <span
                v-for="c in colorSwatches"
                :key="c"
                class="color-swatch"
                :style="{ background: c }"
                :class="{ active: eventForm.color === c }"
                @click="eventForm.color = c"
              ></span>
            </div>
          </div>

          <div class="form-group">
            <input
              v-model="eventForm.location_text"
              type="text"
              class="form-control"
              placeholder="Location (optional)"
            />
          </div>

          <div class="form-group">
            <textarea
              v-model="eventForm.description"
              class="form-control"
              placeholder="Description (optional)"
              rows="3"
            ></textarea>
          </div>

          <!-- ── Recurrence ─────────────────────────────────────── -->
          <div class="form-group recurrence-section">
            <label class="form-label-sm">Repeat</label>
            <select v-model="eventForm.recurrence_type" class="form-control form-select">
              <option value="none">Does not repeat</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <template v-if="eventForm.recurrence_type !== 'none'">
            <!-- Interval -->
            <div class="form-group recurrence-row">
              <label class="form-label-sm">Every</label>
              <div class="recurrence-interval-wrap">
                <input
                  type="number"
                  v-model.number="eventForm.recurrence_interval"
                  min="1" max="99"
                  class="form-control recurrence-num"
                />
                <span class="recurrence-unit">{{ intervalLabel }}</span>
              </div>
            </div>

            <!-- Weekly day picker -->
            <div class="form-group" v-if="eventForm.recurrence_type === 'weekly'">
              <label class="form-label-sm">On</label>
              <div class="day-picker">
                <button
                  v-for="(d, i) in ['S','M','T','W','T','F','S']"
                  :key="i"
                  type="button"
                  class="day-btn"
                  :class="{ active: eventForm.recurrence_days.includes(i) }"
                  @click="toggleRecurrenceDay(i)"
                >{{ d }}</button>
              </div>
            </div>

            <!-- End condition -->
            <div class="form-group">
              <label class="form-label-sm">Ends</label>
              <div class="recurrence-end-options">
                <label class="recurrence-end-option">
                  <input type="radio" v-model="eventForm.recurrence_end_type" value="never" />
                  <span>Never</span>
                </label>
                <label class="recurrence-end-option">
                  <input type="radio" v-model="eventForm.recurrence_end_type" value="date" />
                  <span>On date</span>
                </label>
                <label class="recurrence-end-option">
                  <input type="radio" v-model="eventForm.recurrence_end_type" value="count" />
                  <span>After</span>
                </label>
              </div>
              <div v-if="eventForm.recurrence_end_type === 'date'" class="mt-2">
                <input type="date" v-model="eventForm.recurrence_end_date" class="form-control" />
              </div>
              <div v-if="eventForm.recurrence_end_type === 'count'" class="mt-2 recurrence-interval-wrap">
                <input
                  type="number"
                  v-model.number="eventForm.recurrence_count"
                  min="1" max="365"
                  class="form-control recurrence-num"
                />
                <span class="recurrence-unit">occurrences</span>
              </div>
            </div>
          </template>

          <button
            class="btn-save-event"
            @click="saveEvent(pendingRecurrenceScope || 'this'); pendingRecurrenceScope = null"
            :disabled="savingEvent"
          >
            <span v-if="savingEvent">
              <span class="spinner-border spinner-border-sm"></span> Saving...
            </span>
            <span v-else>
              <ion-icon name="checkmark-outline"></ion-icon>
              {{ editingEvent ? 'Update Event' : 'Save Event' }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── MY CALENDARS SHEET ──────────────────────────────────────────── -->
    <div v-if="showCalendarSheet" class="sheet-overlay" @click.self="showCalendarSheet = false">
      <div class="bottom-sheet">
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <h3 class="sheet-title">My Calendars</h3>
          <button class="sheet-close" @click="showCalendarSheet = false">
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>
        <div class="sheet-body">
          <div v-if="calendarGroups.length === 0" class="text-center py-3 text-muted">
            <div class="spinner-border spinner-border-sm"></div>
          </div>

          <div v-else>
            <!-- Personal + private groups -->
            <p class="calendar-section-label">My Calendars</p>
            <template v-for="g in calendarGroups" :key="g.id">
              <div v-if="g.type !== 'public'" class="calendar-toggle-item" @click="toggleGroup(g.id)">
                <span class="cal-color-dot" :style="{ background: g.color }"></span>
                <span class="cal-name">{{ g.name }}</span>
                <span class="cal-check">
                  <ion-icon v-if="activeGroups.has(g.id)" name="checkbox-outline" :style="{ color: g.color }"></ion-icon>
                  <ion-icon v-else name="square-outline" style="color:#bbb;"></ion-icon>
                </span>
              </div>
            </template>

            <!-- Public groups -->
            <template v-if="calendarGroups.some(g => g.type === 'public')">
              <p class="calendar-section-label" style="margin-top:12px;">Other Calendars</p>
              <template v-for="g in calendarGroups" :key="'pub-' + g.id">
                <div v-if="g.type === 'public'" class="calendar-toggle-item" @click="toggleGroup(g.id)">
                  <span class="cal-color-dot" :style="{ background: g.color }"></span>
                  <span class="cal-name">{{ g.name }}</span>
                  <span class="cal-check">
                    <ion-icon v-if="activeGroups.has(g.id)" name="checkbox-outline" :style="{ color: g.color }"></ion-icon>
                    <ion-icon v-else name="square-outline" style="color:#bbb;"></ion-icon>
                  </span>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ── FILTER SHEET ────────────────────────────────────────────────── -->
    <div v-if="showFilterSheet" class="sheet-overlay" @click.self="cancelFilter">
      <div class="bottom-sheet bottom-sheet-form">
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <h3 class="sheet-title">Filter Events</h3>
          <button class="sheet-close" @click="cancelFilter">
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>
        <div class="sheet-body">

          <!-- Wing filter -->
          <div class="filter-section">
            <p class="filter-section-label">Wing</p>
            <div class="wing-pills">
              <button
                class="wing-pill"
                :class="{ active: draftFilters.wing === null }"
                @click="draftFilters.wing = null"
              >All</button>
              <button
                v-for="(label, num) in wingLabels"
                :key="num"
                class="wing-pill"
                :class="{ active: draftFilters.wing === num }"
                @click="draftFilters.wing = num"
              >{{ label }}</button>
            </div>
          </div>

          <!-- Location filter: District → Zone → Unit -->
          <div class="filter-section">
            <p class="filter-section-label">Location</p>

            <!-- District -->
            <div class="form-group">
              <label class="form-label-sm">District</label>
              <select
                class="form-control form-select"
                v-model="draftFilters.district_id"
                @change="onDraftDistrictChange"
              >
                <option :value="null">All Districts</option>
                <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>

            <!-- Zone (loads when district selected) -->
            <div class="form-group" v-if="draftFilters.district_id">
              <label class="form-label-sm">Zone</label>
              <div v-if="loadingZones" class="text-center py-2">
                <div class="spinner-border spinner-border-sm text-secondary"></div>
              </div>
              <select v-else class="form-control form-select" v-model="draftFilters.zone_id" @change="onDraftZoneChange">
                <option :value="null">All Zones</option>
                <option v-for="z in draftZones" :key="z.id" :value="z.id">{{ z.name }}</option>
              </select>
            </div>

            <!-- Unit (loads when zone selected) -->
            <div class="form-group" v-if="draftFilters.zone_id">
              <label class="form-label-sm">Unit</label>
              <div v-if="loadingUnits" class="text-center py-2">
                <div class="spinner-border spinner-border-sm text-secondary"></div>
              </div>
              <select v-else class="form-control form-select" v-model="draftFilters.unit_id">
                <option :value="null">All Units</option>
                <option v-for="u in draftUnits" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="filter-actions">
            <button class="btn-filter-reset" @click="resetDraftToDefault">
              <ion-icon name="refresh-outline"></ion-icon> Reset
            </button>
            <button class="btn-filter-apply" @click="applyFilter">
              <ion-icon name="checkmark-outline"></ion-icon> Apply
            </button>
          </div>

        </div>
      </div>
    </div>

    <!-- ── MEMBER SWITCHER SHEET ───────────────────────────────────────── -->
    <div v-if="showMemberSheet" class="sheet-overlay" @click.self="showMemberSheet = false">
      <div class="bottom-sheet">
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <h3 class="sheet-title">View Calendar As</h3>
          <button class="sheet-close" @click="showMemberSheet = false">
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>
        <div class="sheet-body">
          <div
            class="member-switch-item"
            :class="{ active: !targetMemberId }"
            @click="switchMember(null, 'My Calendar')"
          >
            <ion-icon name="person-circle-outline"></ion-icon>
            <span>My Calendar</span>
            <ion-icon v-if="!targetMemberId" name="checkmark-outline" class="check-icon"></ion-icon>
          </div>
          <div
            v-for="m in managedMembers"
            :key="m.member_id"
            class="member-switch-item"
            :class="{ active: targetMemberId === m.member_id }"
            @click="switchMember(m.member_id, m.name)"
          >
            <ion-icon name="person-outline"></ion-icon>
            <div class="member-switch-info">
              <span>{{ m.name }}</span>
              <small class="permission-badge">{{ m.permission_level }}</small>
            </div>
            <ion-icon v-if="targetMemberId === m.member_id" name="checkmark-outline" class="check-icon"></ion-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- ── DAY VIEW SHEET ────────────────────────────────────────────────── -->
    <div v-if="showDayView" class="sheet-overlay" @click.self="showDayView = false">
      <div class="bottom-sheet bottom-sheet-dayview">
        <div class="sheet-handle"></div>

        <!-- Header: day nav -->
        <div class="dayview-header">
          <button class="nav-arrow-sm" @click="prevDay">
            <ion-icon name="chevron-back-outline"></ion-icon>
          </button>
          <div class="dayview-title">
            <span class="dayview-dow">{{ dayViewMoment.format('dddd') }}</span>
            <span class="dayview-date">{{ dayViewMoment.format('D MMMM YYYY') }}</span>
          </div>
          <button class="nav-arrow-sm" @click="nextDay">
            <ion-icon name="chevron-forward-outline"></ion-icon>
          </button>
          <button class="sheet-close" @click="showDayView = false">
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>

        <!-- All-day + multi-day events strip -->
        <div class="dayview-allday" v-if="dayViewAllDay.length > 0">
          <div
            v-for="e in dayViewAllDay"
            :key="'allday-' + e.id"
            class="dayview-allday-chip"
            :style="{ background: e.color || '#3788d8' }"
            @click="openEventDetail(e)"
          >
            <span class="allday-title">{{ e.title }}</span>
            <span v-if="!e.is_all_day" class="allday-range">
              {{ formatMultiDayRange(e) }}
            </span>
          </div>
        </div>

        <!-- Hourly grid -->
        <div class="dayview-scroll" ref="dayViewScroll">
          <div class="dayview-hour-grid">
            <!-- Hour rows (background) -->
            <div v-for="h in 24" :key="'hr-' + h" class="hour-row">
              <div class="hour-label">{{ formatHour(h - 1) }}</div>
              <div class="hour-slot"></div>
            </div>

            <!-- Current time line (today only) -->
            <div v-if="dayViewIsToday" class="now-line" :style="{ top: nowLineTop + 'px' }">
              <span class="now-dot"></span>
            </div>

            <!-- Timed events (side-by-side when overlapping) -->
            <div
              v-for="e in dayViewTimedWithLayout"
              :key="'dv-' + e.id"
              class="dayview-event"
              :style="getDayViewEventStyle(e)"
              @click="openEventDetail(e)"
            >
              <span class="dv-event-title">{{ e.title }}</span>
              <span class="dv-event-time">{{ dayViewEventTimeLabel(e) }}</span>
            </div>
          </div>
        </div>

        <!-- Add event FAB inside day view -->
        <button class="dayview-fab" @click="showDayView = false; openAddSheet()">
          <ion-icon name="add-outline"></ion-icon>
        </button>
      </div>
    </div>

    <!-- ── EVENT DETAIL SHEET (last in DOM → highest stacking order) ─────── -->
    <div v-if="showEventDetail" class="sheet-overlay" @click.self="showEventDetail = null">
      <div class="bottom-sheet">
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <div class="sheet-color-bar" :style="{ background: showEventDetail.color || '#3788d8' }"></div>
          <div class="sheet-title-wrap">
            <h3 class="sheet-title">{{ showEventDetail.title }}</h3>
            <span v-if="showEventDetail.group_name" class="group-badge-lg" :style="{ background: getGroupColor(showEventDetail.group_id) + '22', color: getGroupColor(showEventDetail.group_id) }">
              {{ showEventDetail.group_name }}
            </span>
            <span v-if="showEventDetail.is_personal" class="group-badge-lg personal-badge">Personal</span>
          </div>
          <button class="sheet-close" @click="showEventDetail = null">
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>
        <div class="sheet-body">
          <div class="detail-row" v-if="!showEventDetail.is_all_day">
            <ion-icon name="time-outline"></ion-icon>
            <!-- Same day: "3 May · 09:00 — 17:00" -->
            <span v-if="formatDate(showEventDetail.start_datetime) === formatDate(showEventDetail.end_datetime)">
              {{ formatDateTime(showEventDetail.start_datetime) }} — {{ formatTime(showEventDetail.end_datetime) }}
            </span>
            <!-- Multi-day: "3 May · 09:00  →  5 May · 17:00" -->
            <span v-else>
              {{ formatDateTime(showEventDetail.start_datetime) }}&nbsp;→&nbsp;{{ formatDateTime(showEventDetail.end_datetime) }}
            </span>
          </div>
          <div class="detail-row" v-else>
            <ion-icon name="calendar-outline"></ion-icon>
            <!-- All-day same day -->
            <span v-if="formatDate(showEventDetail.start_datetime) === formatDate(showEventDetail.end_datetime)">
              All day · {{ formatDate(showEventDetail.start_datetime) }}
            </span>
            <!-- All-day multi-day -->
            <span v-else>
              All day · {{ formatDate(showEventDetail.start_datetime) }} → {{ formatDate(showEventDetail.end_datetime) }}
            </span>
          </div>
          <div class="detail-row" v-if="showEventDetail.location_text">
            <ion-icon name="location-outline"></ion-icon>
            <span>{{ showEventDetail.location_text }}</span>
          </div>
          <div class="detail-row description-row" v-if="showEventDetail.description">
            <ion-icon name="document-text-outline"></ion-icon>
            <span>{{ showEventDetail.description }}</span>
          </div>
          <div class="detail-row" v-if="showEventDetail.is_recurring">
            <ion-icon name="repeat-outline"></ion-icon>
            <span class="recurrence-badge-detail">Recurring event</span>
          </div>
          <div class="sheet-actions" v-if="showEventDetail.is_personal">
            <button
              class="btn-sheet-action btn-edit"
              @click="showEventDetail.is_recurring ? promptScope('save', showEventDetail) : (editEvent(showEventDetail), showEventDetail = null)"
            >
              <ion-icon name="pencil-outline"></ion-icon> Edit
            </button>
            <button
              class="btn-sheet-action btn-delete"
              @click="showEventDetail.is_recurring ? promptScope('delete', showEventDetail) : (deleteEvent(showEventDetail), showEventDetail = null)"
            >
              <ion-icon name="trash-outline"></ion-icon> Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── RECURRING SCOPE SHEET ──────────────────────────────────────── -->
    <div v-if="showScopeSheet" class="sheet-overlay" @click.self="showScopeSheet = false">
      <div class="bottom-sheet">
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <h3 class="sheet-title">{{ scopeAction === 'delete' ? 'Delete recurring event' : 'Edit recurring event' }}</h3>
          <button class="sheet-close" @click="showScopeSheet = false">
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>
        <div class="sheet-body">
          <div class="scope-option" @click="confirmScope('this')">
            <ion-icon name="calendar-number-outline"></ion-icon>
            <div class="scope-option-text">
              <strong>This event only</strong>
              <span>Only this occurrence will be {{ scopeAction === 'delete' ? 'deleted' : 'changed' }}</span>
            </div>
          </div>
          <div class="scope-option" @click="confirmScope('following')">
            <ion-icon name="arrow-forward-outline"></ion-icon>
            <div class="scope-option-text">
              <strong>This and following events</strong>
              <span>This and all future occurrences will be {{ scopeAction === 'delete' ? 'deleted' : 'changed' }}</span>
            </div>
          </div>
          <div class="scope-option" @click="confirmScope('all')">
            <ion-icon name="repeat-outline"></ion-icon>
            <div class="scope-option-text">
              <strong>All events in series</strong>
              <span>Every occurrence in this series will be {{ scopeAction === 'delete' ? 'deleted' : 'changed' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment-timezone';
import UserService from '@/services/user.service';

// IST timezone constant
const IST = 'Asia/Kolkata';
// Parse an API datetime (returned as UTC, e.g. "2026-05-06T00:00:00+00:00") → IST moment
const mIST = (d) => moment.utc(d).tz(IST);
// Current time in IST
const nowIST = () => moment().tz(IST);

export default {
  name: 'ClanderCalendar',

  data() {
    return {
      // Navigation
      currentMonth: moment(),
      selectedDate: moment(),

      // Events
      allEvents: [],
      loading: false,

      // Calendar groups (for "My Calendars" panel)
      calendarGroups: [],
      activeGroups: new Set(),

      // Manager features
      managedMembers: [],
      targetMemberId: null,
      targetMemberName: 'My Calendar',

      // Location + wing filters (applied to API)
      filters: { wing: null, district_id: null, zone_id: null, unit_id: null },
      defaultFilters: { wing: null, district_id: null, zone_id: null, unit_id: null },

      // Filter sheet state
      showFilterSheet: false,
      draftFilters: { wing: null, district_id: null, zone_id: null, unit_id: null },
      districts: [],
      draftZones: [],
      draftUnits: [],
      loadingZones: false,
      loadingUnits: false,

      // Display names for active filter chips
      districtName: '',
      zoneName: '',
      unitName: '',

      wingLabels: {
        1: 'Organisation', 2: 'Youth', 3: 'Students',
        4: 'Women', 5: 'Balavedi', 6: 'Girls',
      },

      // Sheets visibility
      showCalendarSheet: false,
      showMemberSheet: false,
      showAddSheet: false,
      showEventDetail: null,
      showDayView: false,
      dayViewDate: null,   // 'YYYY-MM-DD' string

      // Add/Edit form
      editingEvent: null,
      savingEvent: false,
      formErrors: {},
      eventForm: {
        title: '',
        description: '',
        is_all_day: false,
        start_datetime: '',
        end_datetime: '',
        color: '#3788d8',
        location_text: '',
        // Recurrence
        recurrence_type:     'none',
        recurrence_interval: 1,
        recurrence_days:     [],
        recurrence_end_type: 'never',
        recurrence_end_date: '',
        recurrence_count:    10,
      },
      colorSwatches: ['#3788d8', '#4CAF50', '#e53935', '#FF9800', '#9C27B0', '#00BCD4'],

      // Scope selection sheet (edit / delete recurring events)
      showScopeSheet: false,
      scopeAction: null,             // 'save' | 'delete'
      scopeEvent: null,              // event object awaiting scope confirmation
      pendingRecurrenceScope: null,  // scope chosen for the pending edit
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentMonthLabel() {
      return this.currentMonth.format('MMMM YYYY');
    },
    // Build array of weeks; each week = array of 7 day objects
    calendarWeeks() {
      const start    = this.currentMonth.clone().startOf('month').startOf('week');
      const end      = this.currentMonth.clone().endOf('month').endOf('week');
      const today    = nowIST().startOf('day');
      const selKey   = this.selectedDate.format('YYYY-MM-DD');
      const monthNum = this.currentMonth.month();
      const weeks    = [];
      const cur      = start.clone();
      while (cur.isSameOrBefore(end)) {
        const week = [];
        for (let i = 0; i < 7; i++) {
          const key = cur.format('YYYY-MM-DD');
          week.push({
            key,
            day: cur.date(),
            date: key,
            currentMonth: cur.month() === monthNum,
            isToday: cur.isSame(today, 'day'),
            isSelected: key === selKey,
          });
          cur.add(1, 'day');
        }
        weeks.push(week);
      }
      return weeks;
    },

    // For each week, separate multi-day events (continuous bar) from single-day chips
    weeksWithData() {
      return this.calendarWeeks.map(week => {
        const weekStart = moment(week[0].date);
        const weekEnd   = moment(week[6].date);
        const multiDay  = [];
        const singleDay = [[], [], [], [], [], [], []];

        this.visibleEvents.forEach(e => {
          const s  = mIST(e.start_datetime).startOf('day');
          const en = mIST(e.end_datetime).startOf('day');
          if (!s.isSameOrBefore(weekEnd) || !en.isSameOrAfter(weekStart)) return;

          if (en.isAfter(s)) {
            // Multi-day: one continuous bar, clamped to this week's columns
            const colStart = Math.max(0, s.diff(weekStart, 'days'));
            const colEnd   = Math.min(6, en.diff(weekStart, 'days'));
            multiDay.push({ ...e, colStart, colEnd });
          } else {
            // Single-day: chip in its own column only
            const col = s.diff(weekStart, 'days');
            if (col >= 0 && col <= 6) singleDay[col].push(e);
          }
        });

        return { week, multiDay, singleDay };
      });
    },

    // Day view computeds
    dayViewMoment() {
      return this.dayViewDate ? moment(this.dayViewDate) : this.selectedDate.clone();
    },
    dayViewIsToday() {
      return this.dayViewMoment.isSame(nowIST(), 'day');
    },
    dayViewAllEvents() {
      const key = this.dayViewMoment.format('YYYY-MM-DD');
      const events = this.eventsByDate[key] || [];
      return events.slice().sort((a, b) => {
        if (a.is_all_day && !b.is_all_day) return -1;
        if (!a.is_all_day && b.is_all_day) return 1;
        return mIST(a.start_datetime).diff(mIST(b.start_datetime));
      });
    },
    // Only true all-day events go in the strip
    dayViewAllDay() {
      return this.dayViewAllEvents.filter(e => !!e.is_all_day);
    },
    // Every non-all-day event that overlaps the viewed day appears in the hourly grid
    dayViewTimed() {
      const dayStart = this.dayViewMoment.clone().startOf('day');
      const dayEnd   = this.dayViewMoment.clone().endOf('day');
      return this.dayViewAllEvents.filter(e => {
        if (e.is_all_day) return false;
        const s  = mIST(e.start_datetime);
        const en = mIST(e.end_datetime);
        return s.isBefore(dayEnd) && en.isAfter(dayStart);
      });
    },

    // Same list but with overlap layout info (_col, _totalCols) for side-by-side rendering
    dayViewTimedWithLayout() {
      const dayStart = this.dayViewMoment.clone().startOf('day');
      const dayEnd   = this.dayViewMoment.clone().endOf('day');

      if (!this.dayViewTimed.length) return [];

      // Build list with clipped IST moments for layout calculations
      const items = this.dayViewTimed.map(e => {
        let s = mIST(e.start_datetime);
        let en = mIST(e.end_datetime);
        if (s.isBefore(dayStart)) s = dayStart.clone();
        if (en.isAfter(dayEnd))   en = dayEnd.clone();
        return { ...e, _s: s, _e: en, _col: 0, _totalCols: 1 };
      }).sort((a, b) => a._s.diff(b._s));

      // Greedy column assignment — find earliest free column for each event
      const colEnds = []; // colEnds[i] = end time of the last event placed in column i
      items.forEach(ev => {
        let placed = false;
        for (let c = 0; c < colEnds.length; c++) {
          if (!colEnds[c].isAfter(ev._s)) {
            ev._col = c;
            colEnds[c] = ev._e.clone();
            placed = true;
            break;
          }
        }
        if (!placed) {
          ev._col = colEnds.length;
          colEnds.push(ev._e.clone());
        }
      });

      // Second pass: find the total column count for each event's overlap cluster
      // (highest column index among all events that overlap this one) + 1
      items.forEach(ev => {
        let maxCol = ev._col;
        items.forEach(other => {
          if (other === ev) return;
          if (other._s.isBefore(ev._e) && other._e.isAfter(ev._s)) {
            maxCol = Math.max(maxCol, other._col);
          }
        });
        ev._totalCols = maxCol + 1;
      });

      return items;
    },
    nowLineTop() {
      const now = nowIST();
      return ((now.hours() + now.minutes() / 60) * 56); // 56px per hour
    },

    // Client-side filter: only show events from toggled-on groups
    visibleEvents() {
      return this.allEvents.filter(e => {
        if (e.is_personal) return this.activeGroups.has('personal');
        // Group event: if its group isn't in "My Calendars" (e.g. directly-assigned
        // events or groups not yet synced), show it by default so nothing is hidden.
        const inList = this.calendarGroups.some(g => g.id === e.group_id);
        if (!inList) return true;
        return this.activeGroups.has(e.group_id);
      });
    },

    // Map dateKey → array of visible events (multi-day events appear on every day they span)
    eventsByDate() {
      const map = {};
      this.visibleEvents.forEach(e => {
        const start = mIST(e.start_datetime).startOf('day');
        const end   = mIST(e.end_datetime).startOf('day');
        const cur   = start.clone();
        while (cur.isSameOrBefore(end, 'day')) {
          const key = cur.format('YYYY-MM-DD');
          (map[key] = map[key] || []).push(e);
          cur.add(1, 'day');
        }
      });
      return map;
    },

    intervalLabel() {
      return { daily: 'days', weekly: 'weeks', monthly: 'months', yearly: 'years' }[this.eventForm.recurrence_type] || 'days';
    },

    // True when any applied filter differs from the member's defaults
    isFilterActive() {
      const f = this.filters;
      const d = this.defaultFilters;
      return f.wing !== d.wing
        || f.district_id !== d.district_id
        || f.zone_id !== d.zone_id
        || f.unit_id !== d.unit_id;
    },

  },

  watch: {
    currentMonth() {
      this.fetchEvents();
    },
    targetMemberId() {
      this.fetchCalendarGroups();
      this.fetchEvents();
    },
    // Keep the filter badge on the header button in sync
    isFilterActive() {
      this.syncHeaderActions();
    },
    managedMembers() {
      this.syncHeaderActions();
    },
  },

  mounted() {
    if (!this.currentUser) {
      this.$router.push('/');
      return;
    }
    if (this.$root.updateParent) this.$root.updateParent('My Calendar');

    this.syncHeaderActions();
    this.fetchCalendarGroups();
    this.fetchManagedMembers();
    this.fetchDistricts();
    this.fetchEvents();
  },

  unmounted() {
    // Clear header actions when leaving the page
    if (this.$root.updateHeaderActions) this.$root.updateHeaderActions([]);
  },

  methods: {
    // ── DATA FETCHING ──────────────────────────────────────────────────────

    async fetchCalendarGroups() {
      try {
        const url = this.targetMemberId
          ? `clander/event-groups?member_id=${this.targetMemberId}`
          : 'clander/event-groups';
        const res = await UserService.authGetRequest(url);
        this.calendarGroups = res.data.data.groups || [];
        // Default: all groups toggled ON
        const active = new Set();
        this.calendarGroups.forEach(g => active.add(g.id));
        this.activeGroups = active;
      } catch (e) {
        console.error('Failed to load calendar groups', e);
      }
    },

    async fetchManagedMembers() {
      try {
        const res = await UserService.authGetRequest('clander/managed-members');
        this.managedMembers = res.data.data.members || [];
      } catch (e) {
        console.error('Failed to load managed members', e);
      }
    },

    async fetchEvents() {
      this.loading = true;
      try {
        const start = this.currentMonth.clone().startOf('month').startOf('week').format('YYYY-MM-DD');
        const end   = this.currentMonth.clone().endOf('month').endOf('week').format('YYYY-MM-DD');

        const params = { start, end };
        if (this.targetMemberId)       params.member_id   = this.targetMemberId;
        if (this.filters.wing)         params.wing        = this.filters.wing;
        if (this.filters.district_id)  params.district_id = this.filters.district_id;
        if (this.filters.zone_id)      params.zone_id     = this.filters.zone_id;
        if (this.filters.unit_id)      params.unit_id     = this.filters.unit_id;

        const res = await UserService.authGetRequest('clander/events', params);
        this.allEvents = res.data.data.events || [];

        // Merge any groups present in events but missing from calendarGroups.
        // This covers events visible via direct assignment whose group is not
        // returned by the event-groups endpoint.
        this._mergeGroupsFromEvents();

        // On first load (or after a member switch), set default filters from the
        // target member's own location/wing so the filter sheet pre-fills correctly.
        const memberInfo = res.data.data.member;
        if (memberInfo && !this._defaultsLoaded) {
          this._defaultsLoaded = true;
          this.defaultFilters = {
            wing:        memberInfo.wing        || null,
            district_id: memberInfo.district_id || null,
            zone_id:     memberInfo.zone_id     || null,
            unit_id:     memberInfo.unit_id     || null,
          };
          // Apply as current filters → re-fetch so events are scoped to member's location
          this.filters = { ...this.defaultFilters };
          // Only re-fetch if any default is non-null (avoid pointless duplicate call)
          const hasDefaults = Object.values(this.defaultFilters).some(v => v !== null);
          if (hasDefaults) this.fetchEvents();
        }
      } catch (e) {
        console.error('Failed to load events', e);
      } finally {
        this.loading = false;
      }
    },

    // Scan allEvents and inject any group not yet in calendarGroups.
    // Called after fetchEvents so the list stays in sync regardless of
    // how the event became visible (public, assigned, direct, etc.).
    _mergeGroupsFromEvents() {
      const knownIds = new Set(this.calendarGroups.map(g => g.id));
      const extra = [];

      this.allEvents.forEach(e => {
        if (e.is_personal || !e.group_id || knownIds.has(e.group_id)) return;
        knownIds.add(e.group_id); // avoid duplicates within the loop
        extra.push({
          id:        e.group_id,
          name:      e.group_name || `Group ${e.group_id}`,
          color:     e.group_color || '#4CAF50',
          is_public: false,
          type:      'group',
        });
      });

      if (extra.length === 0) return;

      // Append to calendarGroups and activate them
      this.calendarGroups = [...this.calendarGroups, ...extra];
      const active = new Set(this.activeGroups);
      extra.forEach(g => active.add(g.id));
      this.activeGroups = active;
    },

    async fetchDistricts() {
      try {
        const res = await UserService.authGetRequest('location/districts');
        this.districts = res.data.data.districts || [];
      } catch (e) {
        console.error('Failed to load districts', e);
      }
    },

    async fetchDraftZones(districtId) {
      this.loadingZones = true;
      try {
        const res = await UserService.authGetRequest('location/zones', { district_id: districtId });
        this.draftZones = res.data.data.zones || [];
      } catch (e) {
        this.draftZones = [];
      } finally {
        this.loadingZones = false;
      }
    },

    async fetchDraftUnits(zoneId) {
      this.loadingUnits = true;
      try {
        const res = await UserService.authGetRequest('location/units', { zone_id: zoneId });
        this.draftUnits = res.data.data.units || [];
      } catch (e) {
        this.draftUnits = [];
      } finally {
        this.loadingUnits = false;
      }
    },

    // ── HEADER ACTION BUTTONS ───────────────────────────────────────────

    syncHeaderActions() {
      if (!this.$root.updateHeaderActions) return;
      const actions = [
        {
          key: 'calendars',
          icon: 'calendar-outline',
          label: 'Calendars',
          badge: false,
          onClick: () => { this.showCalendarSheet = true; },
        },
        {
          key: 'filter',
          icon: 'funnel-outline',
          label: 'Filter',
          badge: this.isFilterActive,
          onClick: () => { this.openFilterSheet(); },
        },
      ];
      if (this.managedMembers.length > 0) {
        actions.push({
          key: 'members',
          icon: 'people-outline',
          label: this.targetMemberName,
          badge: !!this.targetMemberId,
          onClick: () => { this.showMemberSheet = true; },
        });
      }
      this.$root.updateHeaderActions(actions);
    },

    onHeaderAction(key) {
      if (key === 'calendars') this.showCalendarSheet = true;
      if (key === 'filter')    this.openFilterSheet();
      if (key === 'members')   this.showMemberSheet = true;
    },

    // ── FILTER SHEET ────────────────────────────────────────────────────

    async openFilterSheet() {
      // Clone current applied filters into draft
      this.draftFilters = { ...this.filters };
      this.draftZones = [];
      this.draftUnits = [];
      // Pre-load zones/units for current draft selections
      if (this.draftFilters.district_id) {
        await this.fetchDraftZones(this.draftFilters.district_id);
      }
      if (this.draftFilters.zone_id) {
        await this.fetchDraftUnits(this.draftFilters.zone_id);
      }
      this.showFilterSheet = true;
    },

    cancelFilter() {
      this.showFilterSheet = false;
    },

    async onDraftDistrictChange() {
      // Clear lower levels
      this.draftFilters.zone_id = null;
      this.draftFilters.unit_id = null;
      this.draftZones = [];
      this.draftUnits = [];
      if (this.draftFilters.district_id) {
        await this.fetchDraftZones(this.draftFilters.district_id);
      }
    },

    async onDraftZoneChange() {
      this.draftFilters.unit_id = null;
      this.draftUnits = [];
      if (this.draftFilters.zone_id) {
        await this.fetchDraftUnits(this.draftFilters.zone_id);
      }
    },

    resetDraftToDefault() {
      this.draftFilters = { ...this.defaultFilters };
      this.draftZones = [];
      this.draftUnits = [];
      if (this.draftFilters.district_id) this.fetchDraftZones(this.draftFilters.district_id);
      if (this.draftFilters.zone_id)     this.fetchDraftUnits(this.draftFilters.zone_id);
    },

    applyFilter() {
      this.filters = { ...this.draftFilters };
      this.showFilterSheet = false;
      // Update display names for chips
      const d = this.districts.find(x => x.id === this.filters.district_id);
      const z = this.draftZones.find(x => x.id === this.filters.zone_id);
      const u = this.draftUnits.find(x => x.id === this.filters.unit_id);
      this.districtName = d ? d.name : '';
      this.zoneName     = z ? z.name : '';
      this.unitName     = u ? u.name : '';
      this.fetchEvents();
    },

    resetFilters() {
      this.filters = { ...this.defaultFilters };
      this.districtName = '';
      this.zoneName = '';
      this.unitName = '';
      this.fetchEvents();
    },

    clearWingFilter() {
      this.filters.wing = this.defaultFilters.wing;
      this.fetchEvents();
    },

    clearLocationFilter(level) {
      if (level === 'unit') {
        this.filters.unit_id = null;
        this.unitName = '';
      } else if (level === 'zone') {
        this.filters.zone_id = null;
        this.filters.unit_id = null;
        this.zoneName = '';
        this.unitName = '';
      } else {
        this.filters.district_id = null;
        this.filters.zone_id = null;
        this.filters.unit_id = null;
        this.districtName = '';
        this.zoneName = '';
        this.unitName = '';
      }
      this.fetchEvents();
    },

    // ── CALENDAR NAVIGATION ─────────────────────────────────────────────

    prevMonth() {
      this.currentMonth = this.currentMonth.clone().subtract(1, 'month');
    },
    nextMonth() {
      this.currentMonth = this.currentMonth.clone().add(1, 'month');
    },
    goToday() {
      this.currentMonth = nowIST();
      this.selectedDate = nowIST();
    },
    selectDate(dateStr) {
      this.selectedDate = moment(dateStr);
      this.dayViewDate  = dateStr;
      this.showDayView  = true;
      if (!moment(dateStr).isSame(this.currentMonth, 'month')) {
        this.currentMonth = moment(dateStr);
      }
      // Scroll day view to 8am after render
      this.$nextTick(() => {
        const el = this.$refs.dayViewScroll;
        if (el) el.scrollTop = 8 * 56; // 8 hours × 56px
      });
    },

    prevDay() {
      const d = this.dayViewMoment.clone().subtract(1, 'day');
      this.dayViewDate  = d.format('YYYY-MM-DD');
      this.selectedDate = d.clone();
      if (!d.isSame(this.currentMonth, 'month')) this.currentMonth = d.clone();
      this.$nextTick(() => {
        const el = this.$refs.dayViewScroll;
        if (el) el.scrollTop = 8 * 56;
      });
    },

    nextDay() {
      const d = this.dayViewMoment.clone().add(1, 'day');
      this.dayViewDate  = d.format('YYYY-MM-DD');
      this.selectedDate = d.clone();
      if (!d.isSame(this.currentMonth, 'month')) this.currentMonth = d.clone();
      this.$nextTick(() => {
        const el = this.$refs.dayViewScroll;
        if (el) el.scrollTop = 8 * 56;
      });
    },

    // Time label shown inside a day-view event chip — indicates multi-day continuation
    dayViewEventTimeLabel(event) {
      const dayStart = this.dayViewMoment.clone().startOf('day');
      const dayEnd   = this.dayViewMoment.clone().endOf('day');
      const s        = mIST(event.start_datetime);
      const e        = mIST(event.end_datetime);
      const startsBefore = s.isBefore(dayStart);
      const endsAfter    = e.isAfter(dayEnd);
      const sLabel = startsBefore ? '00:00' : s.format('HH:mm');
      const eLabel = endsAfter    ? '23:59' : e.format('HH:mm');
      let suffix = '';
      if (startsBefore && endsAfter)      suffix = ' (continues)';
      else if (startsBefore)              suffix = ` (from ${s.format('D MMM')})`;
      else if (endsAfter)                 suffix = ` → ${e.format('D MMM HH:mm')}`;
      return `${sLabel}–${eLabel}${suffix}`;
    },

    formatMultiDayRange(event) {
      const s = mIST(event.start_datetime);
      const e = mIST(event.end_datetime);
      const sameDay = s.isSame(e, 'day');
      if (sameDay) return `${s.format('HH:mm')}–${e.format('HH:mm')}`;
      return `${s.format('D MMM HH:mm')} – ${e.format('D MMM HH:mm')}`;
    },

    formatHour(h) {
      if (h === 0) return '12 am';
      if (h < 12) return `${h} am`;
      if (h === 12) return '12 pm';
      return `${h - 12} pm`;
    },

    getDayViewEventStyle(event) {
      const HOUR_H   = 56;  // px per hour — must match CSS
      const LABEL_W  = 58;  // px — .hour-label width (52) + 6px gap
      const RIGHT_PAD = 8;  // px — from CSS right: 8px
      const GUTTER   = 3;   // px gap between side-by-side columns

      const dayStart = this.dayViewMoment.clone().startOf('day');
      const dayEnd   = this.dayViewMoment.clone().endOf('day');
      // Use pre-clipped moments if available (from dayViewTimedWithLayout), else clip now
      let s = event._s ? event._s.clone() : mIST(event.start_datetime);
      let e = event._e ? event._e.clone() : mIST(event.end_datetime);
      if (!event._s && s.isBefore(dayStart)) s = dayStart.clone();
      if (!event._e && e.isAfter(dayEnd))    e = dayEnd.clone();

      const startPx  = (s.hours() + s.minutes() / 60) * HOUR_H;
      const durMin   = Math.max(e.diff(s, 'minutes'), 30);
      const color    = event.color || '#3788d8';
      const totalCols = event._totalCols || 1;
      const col       = event._col       || 0;
      const overlapping = totalCols > 1;

      // Position & size
      const colWidthCalc = `(100% - ${LABEL_W + RIGHT_PAD}px) / ${totalCols}`;
      const leftPx = overlapping
        ? `calc(${LABEL_W}px + ${col} * (${colWidthCalc}) + ${col > 0 ? GUTTER : 0}px)`
        : undefined; // let CSS left: 58px handle it
      const widthVal = overlapping
        ? `calc(${colWidthCalc} - ${GUTTER}px)`
        : undefined; // let CSS right: 8px handle it

      // All events: semi-transparent background so hour grid lines show through.
      // Bold colored left border + darkened text keeps the event clearly identifiable.
      const bgColor   = this.hexToRgba(color, 0.15);
      const textColor = this.shadeColor(color, -40);
      const borderCol = color;

      const style = {
        top:        startPx + 'px',
        height:     (durMin / 60 * HOUR_H) + 'px',
        background: bgColor,
        borderLeft: `3px solid ${borderCol}`,
        color:      textColor,
      };
      if (leftPx)  { style.left = leftPx; style.right = 'auto'; }
      if (widthVal) { style.width = widthVal; }
      return style;
    },

    // Convert hex color to rgba with the given opacity (0–1)
    hexToRgba(hex, alpha) {
      let h = hex.replace('#', '');
      if (h.length === 3) h = h.split('').map(c => c + c).join('');
      const num = parseInt(h, 16);
      const r = (num >> 16) & 0xff;
      const g = (num >> 8)  & 0xff;
      const b =  num        & 0xff;
      return `rgba(${r},${g},${b},${alpha})`;
    },

    // Lighten a hex colour toward white — pct: 0=unchanged, 100=white
    lightenColor(hex, pct) {
      let h = hex.replace('#', '');
      if (h.length === 3) h = h.split('').map(c => c + c).join('');
      const num = parseInt(h, 16);
      const r = (num >> 16) & 0xff;
      const g = (num >> 8)  & 0xff;
      const b =  num        & 0xff;
      const R = Math.round(r + (255 - r) * pct / 100);
      const G = Math.round(g + (255 - g) * pct / 100);
      const B = Math.round(b + (255 - b) * pct / 100);
      return '#' + ((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1);
    },

    // Darken a hex color by `percent` (negative = darker)
    shadeColor(hex, percent) {
      let h = hex.replace('#', '');
      if (h.length === 3) h = h.split('').map(c => c + c).join('');
      const num = parseInt(h, 16);
      const amt = Math.round(2.55 * percent);
      const R = Math.max(0, Math.min(255, (num >> 16) + amt));
      const G = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amt));
      const B = Math.max(0, Math.min(255, (num & 0xff) + amt));
      return '#' + ((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1);
    },

    // ── GROUP TOGGLING ──────────────────────────────────────────────────

    toggleGroup(groupId) {
      const active = new Set(this.activeGroups);
      if (active.has(groupId)) {
        active.delete(groupId);
      } else {
        active.add(groupId);
      }
      this.activeGroups = active;
    },

    getGroupColor(groupId) {
      const g = this.calendarGroups.find(g => g.id === groupId);
      return g ? g.color : '#3788d8';
    },

    // ── MEMBER SWITCHING ────────────────────────────────────────────────

    switchMember(memberId, memberName) {
      this.showMemberSheet = false;

      if (memberId) {
        // Set filters from the managed member's own location before fetchEvents fires
        const m = this.managedMembers.find(x => x.member_id === memberId);
        if (m) {
          this.defaultFilters = {
            district_id: m.district_id || null,
            zone_id:     m.zone_id     || null,
            unit_id:     m.unit_id     || null,
            wing:        m.wing        || null,
          };
          this.filters        = { ...this.defaultFilters };
          this._defaultsLoaded = true; // don't let fetchEvents overwrite these
        } else {
          // Not in list — fall back to event-response defaults
          this._defaultsLoaded = false;
          this.filters = { wing: null, district_id: null, zone_id: null, unit_id: null };
        }
      } else {
        // Back to own calendar — let fetchEvents reload own defaults
        this._defaultsLoaded = false;
        this.filters = { wing: null, district_id: null, zone_id: null, unit_id: null };
      }

      this.targetMemberId   = memberId;
      this.targetMemberName = memberName;
    },

    // ── EVENT DETAIL ────────────────────────────────────────────────────

    openEventDetail(event) {
      this.showEventDetail = event;
    },

    // ── ADD / EDIT EVENT ────────────────────────────────────────────────

    openAddSheet(event = null) {
      this.editingEvent = event;
      this.formErrors = {};

      if (event) {
        // Pre-fill for edit — convert stored UTC datetimes to IST for display in the form
        const fmt = event.is_all_day ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm';
        const toIST = (iso) => moment.utc(iso).tz('Asia/Kolkata').format(fmt);
        this.eventForm = {
          title:               event.title,
          description:         event.description || '',
          is_all_day:          event.is_all_day,
          start_datetime:      toIST(event.start_datetime),
          end_datetime:        toIST(event.end_datetime),
          color:               event.color || '#3788d8',
          location_text:       event.location_text || '',
          // Recurrence (pre-fill seed's rule; instances don't carry it, default to none)
          recurrence_type:     event.recurrence_type     || 'none',
          recurrence_interval: event.recurrence_interval || 1,
          recurrence_days:     event.recurrence_days     || [],
          recurrence_end_type: event.recurrence_end_type || 'never',
          recurrence_end_date: event.recurrence_end_date || '',
          recurrence_count:    event.recurrence_count    || 10,
        };
      } else {
        // Pre-fill start with selected date
        const fmt = 'YYYY-MM-DDTHH:mm';
        const start = this.selectedDate.clone().hour(9).minute(0);
        this.eventForm = {
          title:               '',
          description:         '',
          is_all_day:          false,
          start_datetime:      start.format(fmt),
          end_datetime:        start.clone().add(1, 'hour').format(fmt),
          color:               '#3788d8',
          location_text:       '',
          recurrence_type:     'none',
          recurrence_interval: 1,
          recurrence_days:     [],
          recurrence_end_type: 'never',
          recurrence_end_date: '',
          recurrence_count:    10,
        };
      }
      this.showAddSheet = true;
    },

    editEvent(event) {
      this.openAddSheet(event);
    },

    closeAddSheet() {
      this.showAddSheet = false;
      this.editingEvent = null;
      this.formErrors = {};
    },

    validateForm() {
      const errors = {};
      if (!this.eventForm.title.trim()) errors.title = 'Title is required.';
      if (!this.eventForm.start_datetime) errors.start_datetime = 'Start date/time is required.';
      if (!this.eventForm.end_datetime) errors.end_datetime = 'End date/time is required.';
      if (this.eventForm.start_datetime && this.eventForm.end_datetime) {
        const s = moment.tz(this.eventForm.start_datetime, 'YYYY-MM-DDTHH:mm', IST);
        const e = moment.tz(this.eventForm.end_datetime,   'YYYY-MM-DDTHH:mm', IST);
        if (e.isBefore(s)) {
          errors.end_datetime = 'End must be after start.';
        }
      }
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },

    async saveEvent(recurrenceScope = 'this') {
      if (!this.validateForm()) return;

      this.savingEvent = true;
      try {
        const payload = {
          title:          this.eventForm.title.trim(),
          description:    this.eventForm.description || null,
          is_all_day:     this.eventForm.is_all_day ? 1 : 0,
          color:          this.eventForm.color,
          location_text:  this.eventForm.location_text || null,
          member_id:      this.targetMemberId || null,
        };

        // Format datetime for backend.
        if (this.eventForm.is_all_day) {
          payload.start_datetime = this.eventForm.start_datetime + ' 00:00:00';
          payload.end_datetime   = this.eventForm.end_datetime   + ' 23:59:59';
        } else {
          payload.start_datetime = moment.tz(this.eventForm.start_datetime, 'YYYY-MM-DDTHH:mm', IST).utc().format('YYYY-MM-DD HH:mm:ss');
          payload.end_datetime   = moment.tz(this.eventForm.end_datetime,   'YYYY-MM-DDTHH:mm', IST).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        // Recurrence fields
        if (this.eventForm.recurrence_type && this.eventForm.recurrence_type !== 'none') {
          payload.recurrence_type     = this.eventForm.recurrence_type;
          payload.recurrence_interval = this.eventForm.recurrence_interval || 1;
          payload.recurrence_end_type = this.eventForm.recurrence_end_type || 'never';
          if (this.eventForm.recurrence_type === 'weekly') {
            payload.recurrence_days = this.eventForm.recurrence_days;
          }
          if (this.eventForm.recurrence_end_type === 'date' && this.eventForm.recurrence_end_date) {
            payload.recurrence_end_date = this.eventForm.recurrence_end_date;
          }
          if (this.eventForm.recurrence_end_type === 'count') {
            payload.recurrence_count = this.eventForm.recurrence_count;
          }
        } else {
          payload.recurrence_type = 'none';
        }

        if (this.editingEvent) {
          // For recurring events, include scope
          if (this.editingEvent.is_recurring) {
            payload.recurrence_scope = recurrenceScope;
          }
          await UserService.authPutRequest(`clander/personal-events/${this.editingEvent.id}`, payload);
        } else {
          await UserService.authPostRequest('clander/personal-events', payload);
        }

        this.closeAddSheet();
        await this.fetchEvents();
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to save event.';
        this.formErrors.title = msg;
      } finally {
        this.savingEvent = false;
      }
    },

    async deleteEvent(event, recurrenceScope = 'this') {
      // For recurring events the scope sheet already served as confirmation.
      // Only show a native confirm for plain (non-recurring) events.
      if (!event.is_recurring && !confirm(`Delete "${event.title}"?`)) return;
      try {
        const url = event.is_recurring
          ? `clander/personal-events/${event.id}?recurrence_scope=${recurrenceScope}`
          : `clander/personal-events/${event.id}`;
        await UserService.authDeleteRequest(url, {});
        this.showEventDetail = null;
        await this.fetchEvents();
      } catch (e) {
        alert('Failed to delete event.');
      }
    },

    // ── SCOPE SHEET HANDLING ─────────────────────────────────────────────

    promptScope(action, event) {
      this.scopeAction   = action;
      this.showScopeSheet = true;
      this.scopeEvent = event;
    },

    confirmScope(scope) {
      this.showScopeSheet = false;
      const event = this.scopeEvent;
      this.showEventDetail = null;
      if (this.scopeAction === 'delete') {
        this.deleteEvent(event, scope);
      } else {
        // Open edit sheet, then hook into saveEvent
        this.editEvent(event);
        // Store scope so saveEvent can pick it up when called
        this.pendingRecurrenceScope = scope;
      }
    },

    toggleRecurrenceDay(dayIndex) {
      const days = [...this.eventForm.recurrence_days];
      const i = days.indexOf(dayIndex);
      if (i === -1) {
        days.push(dayIndex);
      } else {
        days.splice(i, 1);
      }
      this.eventForm.recurrence_days = days.sort((a, b) => a - b);
    },

    // ── FORMATTING HELPERS ──────────────────────────────────────────────

    formatTime(iso) {
      return mIST(iso).format('HH:mm');
    },
    formatDate(iso) {
      return mIST(iso).format('D MMM YYYY');
    },
    formatDateTime(iso) {
      return mIST(iso).format('D MMM · HH:mm');
    },

  },
};
</script>

<style scoped>
/* ── LAYOUT ────────────────────────────────────────────────────────────────── */
.clander-page {
  min-height: 100vh;
  background: #fff;
  /* 57px nav bar + extra breathing room */
  padding-bottom: 90px;
}

/* ── TOP BAR — month nav only ─────────────────────────────────────────────── */
.clander-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px 4px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.topbar-month-center {
  display: flex;
  align-items: center;
  gap: 10px;
}

.month-label {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
}

.today-chip {
  background: #e8f0fe;
  color: #2355b3;
  border: none;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  cursor: pointer;
}
.today-chip:active { background: #d0e1fb; }

.nav-arrow {
  background: none;
  border: none;
  padding: 6px 8px;
  cursor: pointer;
  color: #2355b3;
  font-size: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
}
.nav-arrow:active { background: #f0f4ff; }

/* ── ACTIVE FILTER CHIPS ────────────────────────────────────────────────── */
.filter-chips-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px 4px;
  overflow-x: auto;
  flex-wrap: nowrap;
  background: #f9faff;
  border-bottom: 1px solid #eef0f8;
}
.filter-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #e8f0fe;
  color: #2355b3;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px 4px 6px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}
.filter-chip ion-icon { font-size: 13px; }
.chip-close {
  background: none;
  border: none;
  padding: 0;
  margin-left: 2px;
  cursor: pointer;
  color: #2355b3;
  display: flex;
  font-size: 14px;
  line-height: 1;
}
.chip-reset {
  background: none;
  border: 1px solid #ccc;
  color: #888;
  font-size: 11px;
  padding: 3px 9px;
  border-radius: 20px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}
.chip-reset:active { background: #f0f0f0; }

/* ── VIEWING AS BANNER ──────────────────────────────────────────────────────── */
.viewing-as-bar {
  background: #fff8e1;
  border-bottom: 1px solid #ffe082;
  padding: 6px 14px;
  font-size: 13px;
  color: #555;
  display: flex;
  align-items: center;
  gap: 6px;
}
.viewing-as-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  color: #888;
  font-size: 18px;
  display: flex;
}

/* ── CALENDAR GRID ─────────────────────────────────────────────────────────── */
.clander-grid-wrap {
  padding: 4px 4px 0;
  position: relative;
}

.dow-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 2px;
}
.dow-cell {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  padding: 2px 0;
}

.grid-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.7);
  z-index: 5;
}

/* ── WEEK ROWS ──────────────────────────────────────────────────────────────── */
.week-row {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 3px;
  margin-bottom: 1px;
}

.week-day-nums {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.day-num-cell {
  display: flex;
  justify-content: center;
  padding: 4px 0 2px;
  cursor: pointer;
}

.day-num {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  border-radius: 50%;
  color: #1a1a2e;
}

.day-num-cell.other-month .day-num { color: #ccc; }
.day-num.today {
  background: #e53935;
  color: #fff;
  font-weight: 700;
}
.day-num.selected:not(.today) {
  background: #2355b3;
  color: #fff;
}

/* ── MULTI-DAY BARS ─────────────────────────────────────────────────────────── */
.week-multiday {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 2px;
  padding: 1px 2px;
}

.multiday-bar {
  box-sizing: border-box;
  max-width: 100%;
  color: #fff;
  font-size: 9.5px;
  font-weight: 500;
  padding: 2px 4px;
  border-radius: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  cursor: pointer;
  margin: 0 1px;
}
.multiday-bar:active { opacity: 0.8; }

.week-more-bar {
  grid-column: 1 / -1;
  font-size: 9px;
  color: #888;
  font-weight: 600;
  padding: 0 4px;
}

/* ── SINGLE-DAY CHIPS ───────────────────────────────────────────────────────── */
.week-single-chips {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 1px 2px 2px;
}

.cell-chip-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: pointer;
  min-height: 18px;
  min-width: 0;
  overflow: hidden;
}

.cell-event-chip {
  width: 100%;
  font-size: 9.5px;
  font-weight: 500;
  color: #fff;
  padding: 1px 4px;
  border-radius: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  cursor: pointer;
}
.cell-event-chip:active { opacity: 0.8; }

.cell-more {
  font-size: 9px;
  color: #888;
  font-weight: 600;
  padding: 0 2px;
  line-height: 1.4;
}

/* ── DAY VIEW SHEET ─────────────────────────────────────────────────────────── */
.bottom-sheet-dayview {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.dayview-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px 8px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.dayview-title {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.dayview-dow {
  font-size: 11px;
  color: #888;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.dayview-date {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.2;
}

.nav-arrow-sm {
  background: none;
  border: none;
  color: #2355b3;
  font-size: 20px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
}
.nav-arrow-sm:active { background: #f0f4ff; }

.dayview-allday {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 14px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}
.dayview-allday-chip {
  color: #fff;
  padding: 4px 10px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  max-width: 100%;
}
.dayview-allday-chip:active { opacity: 0.85; }
.allday-title {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.allday-range {
  font-size: 9.5px;
  font-weight: 500;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dayview-scroll {
  flex: 1;
  overflow-y: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
}

.dayview-hour-grid {
  position: relative;
  min-height: calc(24 * 56px); /* 24h × 56px per hour */
}

.hour-row {
  display: flex;
  height: 56px;
  border-bottom: 1px solid #f5f5f5;
}
.hour-label {
  width: 52px;
  flex-shrink: 0;
  font-size: 10px;
  color: #bbb;
  padding: 4px 8px 0 0;
  text-align: right;
  line-height: 1.2;
}
.hour-slot {
  flex: 1;
  border-left: 1px solid #f0f0f0;
}

/* Current time indicator */
.now-line {
  position: absolute;
  left: 52px;
  right: 0;
  height: 2px;
  background: #e53935;
  z-index: 2;
  pointer-events: none;
}
.now-dot {
  position: absolute;
  left: -5px;
  top: -4px;
  width: 10px;
  height: 10px;
  background: #e53935;
  border-radius: 50%;
}

/* Timed events in day view */
.dayview-event {
  position: absolute;
  left: 58px;
  right: 8px;
  border-radius: 5px;
  padding: 3px 7px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  z-index: 1;
  box-sizing: border-box;
}
.dayview-event:active { opacity: 0.85; }

.dv-event-title {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dv-event-time {
  font-size: 10px;
  font-weight: 500;
  opacity: 0.85;
  white-space: nowrap;
}

/* FAB inside day view */
.dayview-fab {
  position: absolute;
  bottom: 16px;
  right: 16px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #2355b3;
  color: #fff;
  border: none;
  font-size: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(35,85,179,0.35);
  cursor: pointer;
  z-index: 10;
}
.dayview-fab:active { opacity: 0.88; }

/* ── (keep existing event-item styles for event detail sheet) ─────────────── */
.all-day-badge {
  background: #e8f0fe;
  color: #2355b3;
  font-size: 10px;
  padding: 2px 5px;
  border-radius: 4px;
}

.event-body { flex: 1; min-width: 0; }
.event-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.event-meta {
  font-size: 11px;
  color: #777;
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 3px;
}
.group-badge {
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 10px;
  font-weight: 600;
}

.event-actions { display: flex; gap: 4px; align-items: center; }
.event-action-btn {
  background: none;
  border: none;
  color: #888;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
}
.event-action-btn:active { background: #f0f0f0; }
.event-action-btn.delete-btn:active { color: #e53935; }

/* ── FAB ─────────────────────────────────────────────────────────────────────── */
.clander-fab {
  position: fixed;
  /* sit 8px above the 57px nav bar */
  bottom: 72px;
  right: 18px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #2355b3;
  color: #fff;
  border: none;
  font-size: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(35,85,179,0.35);
  cursor: pointer;
  z-index: 1000;  /* above nav bar (999) but below sheet overlay (1200) */
  transition: transform 0.15s;
}
.clander-fab:active { transform: scale(0.93); }

/* ── BOTTOM SHEETS ───────────────────────────────────────────────────────────── */
.sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 1200;   /* above appBottomMenu (999) and appHeader (999) */
  display: flex;
  align-items: flex-end;
}

.bottom-sheet {
  background: #fff;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  animation: slideUp 0.25s ease;
  /* push sheet body above the device home indicator / safe area */
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
.bottom-sheet-form { max-height: 92vh; }

@keyframes slideUp {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}

.sheet-handle {
  width: 40px; height: 4px;
  background: #ddd;
  border-radius: 2px;
  margin: 10px auto 0;
}

.sheet-header {
  display: flex;
  align-items: center;
  padding: 14px 16px 10px;
  position: relative;
}
.sheet-color-bar {
  width: 4px; height: 32px;
  border-radius: 2px;
  margin-right: 12px;
  flex-shrink: 0;
}
.sheet-title-wrap { flex: 1; min-width: 0; }
.sheet-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: #1a1a2e;
}
.sheet-close {
  background: none;
  border: none;
  color: #888;
  font-size: 22px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
}

.sheet-body { padding: 4px 16px 24px; }

/* ── EVENT DETAIL SHEET ────────────────────────────────────────────────────── */
.group-badge-lg {
  display: inline-block;
  font-size: 11px;
  padding: 2px 10px;
  border-radius: 10px;
  font-weight: 600;
  margin-top: 4px;
}
.personal-badge { background: #e8f0fe !important; color: #2355b3 !important; }

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 14px;
  color: #333;
}
.detail-row ion-icon { font-size: 18px; color: #888; margin-top: 1px; flex-shrink: 0; }
.description-row { align-items: flex-start; }
.description-row span { white-space: pre-line; }

.sheet-actions { display: flex; gap: 10px; margin-top: 18px; }
.btn-sheet-action {
  flex: 1;
  padding: 11px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.btn-edit { background: #e8f0fe; color: #2355b3; }
.btn-delete { background: #fde8e8; color: #e53935; }

/* ── ADD/EDIT EVENT FORM ────────────────────────────────────────────────────── */
.form-group { margin-bottom: 14px; }

.all-day-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.toggle-label { font-size: 14px; color: #333; }

.form-label-sm { font-size: 12px; color: #666; margin-bottom: 4px; display: block; }

.form-control {
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  width: 100%;
  outline: none;
  transition: border-color 0.15s;
}
.form-control:focus { border-color: #2355b3; }
.form-control.is-invalid { border-color: #e53935; }

.color-swatches { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 4px; }
.color-swatch {
  width: 28px; height: 28px;
  border-radius: 50%;
  cursor: pointer;
  border: 3px solid transparent;
  transition: border-color 0.15s, transform 0.1s;
}
.color-swatch.active { border-color: #1a1a2e; transform: scale(1.18); }

.btn-save-event {
  width: 100%;
  padding: 13px;
  background: #2355b3;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
}
.btn-save-event:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── FILTER SHEET ───────────────────────────────────────────────────────────── */
/* ── Filter sheet: calendar group list ──────────────────────────────────────── */
.filter-group-list { display: flex; flex-direction: column; gap: 2px; }
.filter-group-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 4px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  border-radius: 6px;
}
.filter-group-item:last-child { border-bottom: none; }
.filter-group-item:active { background: #f8f9ff; }
.fg-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.fg-name {
  flex: 1;
  font-size: 14px;
  color: #333;
}
.fg-check { font-size: 20px; }

.filter-section { margin-bottom: 20px; }
.filter-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #aaa;
  letter-spacing: 0.8px;
  margin: 0 0 10px;
}

.wing-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.wing-pill {
  padding: 6px 14px;
  border: 1.5px solid #dde3f0;
  border-radius: 20px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #444;
  cursor: pointer;
  transition: all 0.15s;
}
.wing-pill.active {
  background: #2355b3;
  border-color: #2355b3;
  color: #fff;
  font-weight: 700;
}

/* Explicit select styling — Bootstrap's form-control may not scope into this component */
select.form-control,
.form-select {
  display: block;
  width: 100%;
  padding: 0px 12px !important;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #fff;
  border: 1.5px solid #dde3f0;
  border-radius: 8px;
  appearance: auto;
  -webkit-appearance: auto;
  box-sizing: border-box;
  margin-bottom: 0;
}
select.form-control:focus,
.form-select:focus {
  border-color: #2355b3;
  outline: none;
  box-shadow: 0 0 0 3px rgba(35,85,179,0.12);
}
.filter-section .form-group {
  margin-bottom: 12px;
}

.filter-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}
.btn-filter-reset {
  flex: 1;
  padding: 12px;
  background: #f5f5f5;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.btn-filter-apply {
  flex: 1.4;
  padding: 12px;
  background: #2355b3;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.btn-filter-apply:active { opacity: 0.88; }
.btn-filter-reset:active { background: #e8e8e8; }

/* ── MY CALENDARS SHEET ─────────────────────────────────────────────────────── */
.calendar-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #aaa;
  letter-spacing: 0.8px;
  margin: 8px 0 4px;
}
.calendar-toggle-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 4px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}
.cal-color-dot {
  width: 14px; height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cal-name { flex: 1; font-size: 15px; color: #1a1a2e; }
.cal-check { font-size: 22px; }

/* ── MEMBER SWITCHER ────────────────────────────────────────────────────────── */
.member-switch-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 4px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  font-size: 15px;
  color: #1a1a2e;
}
.member-switch-item ion-icon { font-size: 22px; color: #2355b3; }
.member-switch-item.active { background: #f0f4ff; border-radius: 10px; }
.member-switch-info { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.permission-badge {
  font-size: 10px;
  color: #888;
  text-transform: capitalize;
}
.check-icon { color: #2355b3; font-size: 20px; }

/* ── RECURRENCE UI ──────────────────────────────────────────────────────────── */
.recurrence-section { margin-top: 6px; }
.recurrence-row { display: flex; align-items: center; gap: 10px; }
.recurrence-interval-wrap { display: flex; align-items: center; gap: 8px; }
.recurrence-num { width: 76px !important; text-align: center; }
.recurrence-unit { font-size: 14px; color: #555; white-space: nowrap; }

.day-picker { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px; }
.day-btn {
  width: 36px; height: 36px;
  border-radius: 50%;
  border: 1.5px solid #dde3f0;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: #444;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.day-btn.active {
  background: #2355b3;
  border-color: #2355b3;
  color: #fff;
}

.recurrence-end-options {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.recurrence-end-option {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  cursor: pointer;
  color: #333;
}
.recurrence-end-option input[type="radio"] { cursor: pointer; }

.recurrence-badge-detail {
  font-size: 13px;
  color: #2355b3;
  font-weight: 500;
}

/* ── SCOPE SHEET ────────────────────────────────────────────────────────────── */
.scope-option {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 4px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  transition: background 0.1s;
}
.scope-option:active { background: #f0f4ff; }
.scope-option ion-icon { font-size: 22px; color: #2355b3; flex-shrink: 0; margin-top: 2px; }
.scope-option-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.scope-option-text strong { font-size: 15px; color: #1a1a2e; }
.scope-option-text span { font-size: 13px; color: #888; }
</style>
